import isNil from 'lodash/isNil'
import {MouseEventHandler, useEffect, useMemo} from 'react'
import ReactSelect, {
  MultiValueGenericProps,
  MultiValueProps,
  Props as ReactSelectProps,
  components,
} from 'react-select'
import {
  SortEndHandler,
  SortableContainer,
  SortableContainerProps,
  SortableElement,
  SortableHandle,
  arrayMove,
} from 'react-sortable-hoc'
import useWidth from 'src/hooks/useWidth'
import {SelectOptionBase, SelectProps} from './types'

const Input = props => (
  <components.Input
    {...props}
    inputClassName="outline-none border-none shadow-none focus:ring-transparent"
  />
)

const SortableMultiValue = SortableElement((props: MultiValueProps<any>) => {
  // this prevents the menu from being opened/closed when the user clicks
  // on a value to begin dragging it. ideally, detecting a click (instead of
  // a drag) would still focus the control and toggle the menu, but that
  // requires some magic with refs that are out of scope for this example
  const onMouseDown: MouseEventHandler<HTMLDivElement> = e => {
    e.preventDefault()
    e.stopPropagation()
  }
  const innerProps = {...props.innerProps, onMouseDown}
  return <components.MultiValue {...props} innerProps={innerProps} />
})

const SortableMultiValueLabel = SortableHandle((props: MultiValueGenericProps) => (
  <components.MultiValueLabel {...props} />
))

const SortableSelect = SortableContainer(ReactSelect) as React.ComponentClass<
  ReactSelectProps & SortableContainerProps
>

export default function Select<TOption extends SelectOptionBase, TMulti extends boolean = false>(
  props: SelectProps<TOption, TMulti>,
) {
  const value = useMemo(() => {
    return getValue({value: props.value, options: props.options, multi: props.multi})
  }, [props.value, props.options, props.multi])

  const onChange = params => {
    const {multi} = props
    if (multi) {
      props.onChange(params.map(item => item.value))
    } else {
      if (params && !isNil(params.value)) {
        props.onChange(params.value)
      } else {
        props.onChange(null)
      }
    }
  }

  const onSortEnd: SortEndHandler = ({oldIndex, newIndex}) => {
    if (!props.multi) return
    const newValue = arrayMove(props.value as any, oldIndex, newIndex)
    props.onChange(newValue as any)
  }

  useEffect(() => {
    if (props.setNullOnNoValue === false) return
    if (props.loading) return
    if (props.multi) return
    if (!isNil(value?.value)) return
    if (isNil(props.value)) return

    console.log('Select: onChange(null)', props.value)

    props.onChange(null)
  }, [value?.value, props.loading])

  const width = useWidth()
  const isXs = width < 640

  const Comp = props.multi ? SortableSelect : ReactSelect

  return (
    <div>
      {props.label && (
        <div className="mb-1 block text-sm font-medium text-gray-700">{props.label}</div>
      )}
      <Comp
        useDragHandle={props.multi}
        // react-sortable-hoc props:
        axis="xy"
        onSortEnd={onSortEnd}
        distance={4}
        getHelperDimensions={({node}) => node.getBoundingClientRect()}
        {...props.selectPassProps}
        ref={ref => {
          if (props.inputRef) {
            props.inputRef.current = ref as any
          }
        }}
        autoFocus={props.autoFocus}
        onBlur={props.onBlur}
        filterOption={props.filterOption}
        formatOptionLabel={props.formatOptionLabel}
        value={value}
        onChange={onChange}
        placeholder={props.placeholder}
        isMulti={props.multi}
        isDisabled={props.disabled}
        isLoading={props.loading}
        isClearable={props.isClearable !== false}
        name={props.fieldName}
        options={props.options}
        components={{
          Input,
          MultiValue: SortableMultiValue as any,
          MultiValueLabel: SortableMultiValueLabel as any,
        }}
        noOptionsMessage={props.noOptionsMessage}
        styles={{
          ...props.selectPassProps?.styles,
          control: baseStyles => ({
            ...baseStyles,
            borderRadius: '0.375rem',
            fontSize: isXs ? '16px' : '0.875rem',
            paddingTop: 6,
            paddingBottom: 6,
            marginRight: 4,
            borderColor: '#e2e8f0',
            ...props.selectPassProps?.styles?.control?.(baseStyles, null),
          }),
          menu: baseStyles => ({
            ...baseStyles,
            fontSize: isXs ? '16px' : '0.875rem',
            width: props.menuWidth || baseStyles.width,
            ...props.selectPassProps?.styles?.menu?.(baseStyles, null),
          }),
        }}
      />
      {props.errorMessage ? (
        <p className="mt-2 text-sm text-red-600">{props.errorMessage}</p>
      ) : null}
      {props.description ? <p className="mt-2 text-sm text-gray-500">{props.description}</p> : null}
    </div>
  )
}

function getValue({value, options, multi}) {
  if (multi) {
    const selectedOptions = ((value as string[]) || []).map(optionValue =>
      (options || []).find(option => option.value === optionValue),
    )
    return selectedOptions.filter(option => !!option)
  }
  const selectedOption = (options || []).find(option => option.value === value)
  if (!selectedOption) return null
  return selectedOption
}
